import { useMemo } from "react"
import { ZERO_ADDRESS } from "@mezo/sdk"

import {
  RootState,
  selectTokenDecimals,
  selectWalletBalances,
  useDappSelector,
} from "#/redux-state"
import { SWAP_SLIPPAGE_THRESHOLD } from "#/shared/constants"
import { SwappableToken } from "#/shared/types"
import {
  bigIntToFloat,
  floatToBigInt,
  getMezoTokenAddress,
  getSwapRoutes,
  isStablePool,
  multiplyBigIntWithDecimal,
} from "#/shared/utils"

import { useTokenUsdPrice } from "../token"

import useSwapFee from "./useSwapFee"
import useSwapGas from "./useSwapGas"

const useSwapReview = (
  tokenIn: SwappableToken,
  tokenOut: SwappableToken,
  amountIn: string,
  slippage: number,
) => {
  const fromTokenAddress = getMezoTokenAddress(tokenIn)
  const toTokenAddress = getMezoTokenAddress(tokenOut)

  const tokenDecimals = useDappSelector((state: RootState) =>
    selectTokenDecimals(state, tokenIn),
  )

  const { mBTC } = useDappSelector(selectWalletBalances)

  const { isFetching: isFetchingSwapFee, getSwapFeeAmount } = useSwapFee(
    tokenIn,
    tokenOut,
  )

  const { gasPrice } = useSwapGas(
    floatToBigInt(amountIn, tokenDecimals),
    fromTokenAddress ?? ZERO_ADDRESS,
    getSwapRoutes(
      fromTokenAddress,
      toTokenAddress,
      isStablePool(tokenIn, tokenOut),
    ),
    slippage,
  )

  const transactionFee = useMemo(
    () => getSwapFeeAmount(floatToBigInt(amountIn, tokenDecimals)),
    [amountIn, getSwapFeeAmount, tokenDecimals],
  )

  const { getTokenUsdPrice } = useTokenUsdPrice()

  const transactionTotal = useMemo(() => {
    const networkFeeUsdValue = multiplyBigIntWithDecimal(
      gasPrice ?? 0n,
      getTokenUsdPrice("mBTC"),
    )

    const tokenInUsdPrice = getTokenUsdPrice(tokenIn)

    const swapTokenUsdValue = multiplyBigIntWithDecimal(
      floatToBigInt(amountIn, tokenDecimals),
      tokenInUsdPrice,
    )

    // Ensure that we have the same decimals for swapped token and BTC
    const adjustSwapTokenUsdValue =
      swapTokenUsdValue * 10n ** (18n - BigInt(tokenDecimals))

    return networkFeeUsdValue + adjustSwapTokenUsdValue
  }, [amountIn, tokenIn, tokenDecimals, gasPrice, getTokenUsdPrice])

  return {
    networkFee: bigIntToFloat(gasPrice ?? 0n, Number(mBTC.decimals), 18),
    transactionFee: bigIntToFloat(transactionFee, tokenDecimals, tokenDecimals),
    transactionTotal: bigIntToFloat(transactionTotal, 18, 18),
    isFetchingSwapFee,
    isSlippageTooHigh: slippage >= SWAP_SLIPPAGE_THRESHOLD,
  }
}

export default useSwapReview
